import { useQuery } from '@tanstack/react-query';
import ms from 'ms';
import { type ComponentProps } from 'react';
import { useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { ReactSelectInput } from '@hivebrite/design-system/Inputs';

import { useCurrentNetwork } from 'hb-react/shared/contexts';
import { axiosWithSyntaxHandlers } from 'hb-react/shared/utils/axios';

const ReactSelectPromoCode = ({
  id,
  name,
  ...props
}: ComponentProps<typeof ReactSelectInput>) => {
  const {
    currentNetwork: { id: networkId },
  } = useCurrentNetwork();

  const [inputValue, setInputValue] = useState('');

  const { data: promoCodes = [], isLoading } = useQuery({
    queryKey: ['promoCodes', networkId, inputValue],
    queryFn: () =>
      axiosWithSyntaxHandlers
        .get(Routes.backoffice_network_promo_codes_path(networkId), {
          params: {
            fulltext: inputValue,
            format: 'json',
            per_page: 25,
          },
        })
        .then((res) => res.data.promoCodes || []),
    staleTime: ms('5m'),
  });

  const onInputChange = useDebouncedCallback((value) => {
    setInputValue(value);
  }, 500);

  const options = useMemo(() => {
    const combinedPromoCodes = [
      ...(promoCodes || []),
      ...(Array.isArray(props.value) ? props.value : [props.value]),
    ];
    const uniquePromoCodesMap = new Map();

    combinedPromoCodes.forEach((promoCode) => {
      if (promoCode && typeof promoCode.id !== 'undefined') {
        uniquePromoCodesMap.set(promoCode.id, promoCode);
      }
    });

    return Array.from(uniquePromoCodesMap.values()).map(
      ({ id, label, ...promoCode }) => ({
        value: id,
        label: `${label} - ${promoCode.discountType === 'percentage' ? `${promoCode.valueOff}%` : `$${promoCode.valueOff}`}`,
        ...promoCode,
      }),
    );
  }, [promoCodes, props.value]);

  return (
    <ReactSelectInput
      placeholder={__('Select a promo code')}
      {...props}
      cacheOptions
      isMulti={false}
      isClearable
      id={id}
      name={name}
      options={options}
      isLoading={isLoading}
      // @ts-expect-error react-select is not typed
      onInputChange={onInputChange}
    />
  );
};

export default ReactSelectPromoCode;
